import React, { useState, useRef } from 'react';
import { Screen, AppState } from '../App';
import { User, findStudentByInitials, saveStudent, generateId, StudentProfile, Conversation, saveStrategyAttempt, StrategyAttempt } from '../storage';
import { getFollowUpQuestions, getSummary, streamBrainScience, getStrategies, streamForYou, streamReferral, getMTSSAssessment } from '../api';
import { roleLabels } from '../roleLabels';
import { containsRiskLanguage } from '../safety';
import SafetyInterstitial from '../components/SafetyInterstitial';
import AiDisclaimer from '../components/AiDisclaimer';
import NavBar from '../components/NavBar';

interface Props { navigate: (s: Screen, state?: AppState) => void; user: User | null; }
type Step = 'identify' | 'describe' | 'followup' | 'response';
type Tab = 'summary' | 'brain' | 'strategies' | 'forYou' | 'refer' | 'mtss';

const TAB_ORDER: Tab[] = ['summary', 'brain', 'strategies', 'forYou', 'refer', 'mtss'];

// Grade is a fixed K-12 set (plus Pre-K) so the value is always an unambiguous
// grade — never a bare number the AI could read as an age — and so the same
// student always matches by the same grade string. The stored value is the label
// itself (e.g. "4th grade"), which reads naturally in the AI prompt and profile.
const GRADE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Select grade…' },
  { value: 'Pre-K', label: 'Pre-K' },
  { value: 'Kindergarten', label: 'Kindergarten' },
  { value: '1st grade', label: '1st grade' },
  { value: '2nd grade', label: '2nd grade' },
  { value: '3rd grade', label: '3rd grade' },
  { value: '4th grade', label: '4th grade' },
  { value: '5th grade', label: '5th grade' },
  { value: '6th grade', label: '6th grade' },
  { value: '7th grade', label: '7th grade' },
  { value: '8th grade', label: '8th grade' },
  { value: '9th grade', label: '9th grade' },
  { value: '10th grade', label: '10th grade' },
  { value: '11th grade', label: '11th grade' },
  { value: '12th grade', label: '12th grade' },
];

export default function TalkAboutStudent({ navigate, user }: Props) {
  const L = roleLabels(user);
  const [step, setStep] = useState<Step>('identify');
  const [initials, setInitials] = useState('');
  const [grade, setGrade] = useState('');
  const [description, setDescription] = useState('');
  const [followUps, setFollowUps] = useState<string[]>([]);
  const [fuAnswers, setFuAnswers] = useState<string[]>(['', '']);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<Tab>('summary');
  const [response, setResponse] = useState<any>(null); // { summary, top3 }
  const [existing, setExisting] = useState<StudentProfile | null>(null);
  const [recording, setRecording] = useState(false);
  const [saved, setSaved] = useState(false);
  const [triedStrategies, setTriedStrategies] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [safety, setSafety] = useState(false); // risk-language short-circuit → non-AI routing

  // Per-section state. Each section loads once, on demand, and is then cached.
  const [brain, setBrain] = useState('');
  const [brainLoading, setBrainLoading] = useState(false);
  const [brainErr, setBrainErr] = useState('');
  const [strategies, setStrategies] = useState<any>(null);
  const [strategiesLoading, setStrategiesLoading] = useState(false);
  const [strategiesErr, setStrategiesErr] = useState('');
  const [forYou, setForYou] = useState('');
  const [forYouLoading, setForYouLoading] = useState(false);
  const [forYouErr, setForYouErr] = useState('');
  const [referral, setReferral] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralErr, setReferralErr] = useState('');
  const [mtss, setMtss] = useState<any>(null);
  const [mtssLoading, setMtssLoading] = useState(false);
  const [mtssErr, setMtssErr] = useState('');

  // Teachers must never see raw API errors, status codes, or anything about
  // billing, keys, or accounts. We log the real error to the console (for us to
  // debug) and return ONE warm, plain-language message in the app's voice.
  // The only exception is the gentle "usage limit" message, which is already
  // teacher-safe and genuinely useful to show.
  const WARM_ERROR = "This part didn't come through just now. Give it a moment and tap Retry.";
  const errText = (e: any) => {
    console.error('Decode My Student — AI section error:', e);
    if (e?.code === 'rate_limited') {
      return e?.message || "You've reached the usage limit for now. Take a short break and try again in a little while.";
    }
    return WARM_ERROR;
  };

  // Tracks which sections have already been started, so nothing regenerates.
  const startedRef = useRef<Record<string, boolean>>({});
  const recRef = useRef<any>(null);
  // The shared context (follow-up answers + prior-conversation memory) built
  // once on submit and fed into every section so they all reason from the same
  // full picture. Sections load later on tab-open, so we keep it in a ref.
  const contextRef = useRef<string>('');

  const startRec = (setter: (v: string) => void, cur: string) => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { alert('Voice input not supported in this browser. Please type.'); return; }
    const r = new SR(); r.continuous = true; r.interimResults = true;
    r.onresult = (e: any) => setter(cur + Array.from(e.results).map((x: any) => x[0].transcript).join(''));
    r.start(); recRef.current = r; setRecording(true);
  };
  const stopRec = () => { if (recRef.current) { recRef.current.stop(); recRef.current = null; } setRecording(false); };

  const handleIdentify = () => {
    if (!initials.trim() || !grade.trim()) return;
    if (user) {
      const e = findStudentByInitials(user.id, initials.trim(), grade.trim());
      if (e) {
        setExisting(e);
      } else {
        // Autosave: create the record the moment you identify a student, so the
        // kid is never lost if you're interrupted, navigate away, or hit the
        // safety screen mid-flow. It appears in "My Students" right away; the
        // workup is appended when you Save, and any unfinished section can be
        // generated later from the profile. saveToProfile matches this record
        // (case-insensitive), so there's no duplicate.
        const s: StudentProfile = { id: generateId(), initials: initials.trim(), grade: grade.trim(), userId: user.id, createdAt: Date.now(), conversations: [], wins: [], trend: 'stable' };
        saveStudent(user.id, s);
      }
    }
    setStep('describe');
  };

  const handleDescribe = async () => {
    if (!description.trim()) return;
    // Risk-language short-circuit: route to human help, make NO API call.
    if (containsRiskLanguage(description)) { setSafety(true); return; }
    setError('');
    setLoading(true);
    try {
      const qs = await getFollowUpQuestions(description);
      setFollowUps(qs);
      setFuAnswers(new Array(qs.length).fill(''));
    } catch {
      setFollowUps(["How long has this been happening?", "How is this situation affecting you personally?"]);
    } finally {
      setLoading(false);
    }
    setStep('followup');
  };

  // ---- Section loaders (idempotent: each runs at most once) ----
  const loadBrain = () => {
    if (startedRef.current.brain) return;
    startedRef.current.brain = true;
    setBrainLoading(true); setBrainErr('');
    streamBrainScience(description, initials, grade, setBrain, contextRef.current)
      .then(setBrain)
      .catch((e) => setBrainErr(errText(e)))
      .finally(() => setBrainLoading(false));
  };
  const loadStrategies = () => {
    if (startedRef.current.strategies) return;
    startedRef.current.strategies = true;
    setStrategiesLoading(true); setStrategiesErr('');
    getStrategies(description, initials, grade, contextRef.current)
      .then(setStrategies)
      .catch((e) => setStrategiesErr(errText(e)))
      .finally(() => setStrategiesLoading(false));
  };
  const loadForYou = () => {
    if (startedRef.current.forYou) return;
    startedRef.current.forYou = true;
    setForYouLoading(true); setForYouErr('');
    streamForYou(description, initials, grade, setForYou, contextRef.current)
      .then(setForYou)
      .catch((e) => setForYouErr(errText(e)))
      .finally(() => setForYouLoading(false));
  };
  const loadReferral = () => {
    if (startedRef.current.refer) return;
    startedRef.current.refer = true;
    setReferralLoading(true); setReferralErr('');
    streamReferral(description, initials, grade, setReferral, contextRef.current)
      .then(setReferral)
      .catch((e) => setReferralErr(errText(e)))
      .finally(() => setReferralLoading(false));
  };
  const loadMtss = () => {
    if (startedRef.current.mtss) return;
    startedRef.current.mtss = true;
    setMtssLoading(true); setMtssErr('');
    getMTSSAssessment(description, initials, grade, contextRef.current)
      .then(setMtss)
      .catch((e) => setMtssErr(errText(e)))
      .finally(() => setMtssLoading(false));
  };

  const loaderFor = (t: Tab): (() => void) => {
    switch (t) {
      case 'brain': return loadBrain;
      case 'strategies': return loadStrategies;
      case 'forYou': return loadForYou;
      case 'refer': return loadReferral;
      case 'mtss': return loadMtss;
      default: return () => {};
    }
  };

  const retry = (key: string, loader: () => void) => { startedRef.current[key] = false; loader(); };

  const resetConversation = () => {
    setStep('identify');
    setDescription('');
    setResponse(null);
    setSaved(false);
    setError('');
    setTriedStrategies([]);
    setBrain(''); setBrainLoading(false); setBrainErr('');
    setStrategies(null); setStrategiesLoading(false); setStrategiesErr('');
    setForYou(''); setForYouLoading(false); setForYouErr('');
    setReferral(''); setReferralLoading(false); setReferralErr('');
    setMtss(null); setMtssLoading(false); setMtssErr('');
    startedRef.current = {};
    contextRef.current = '';
  };

  // Build the shared context block once: the teacher's follow-up answers, plus
  // real memory of past conversations (fuller than the old 100-char stub).
  const buildContext = (): string => {
    const parts: string[] = [];

    // #1 — the follow-up answers (previously collected and then discarded).
    const qa = followUps
      .map((q, i) => (fuAnswers[i] && fuAnswers[i].trim() ? `Q: ${q}\nA: ${fuAnswers[i].trim()}` : null))
      .filter(Boolean);
    if (qa.length) parts.push(`Follow-up questions the teacher answered:\n${qa.join('\n')}`);

    // #3 — real memory: fuller slice of each recent past conversation, plus what
    // was suggested last time. Capped at the 5 most recent so the prompt stays sane.
    if (user && existing && existing.conversations.length > 0) {
      const prev = existing.conversations.slice(-5).map((c, idx) => {
        const r: any = c.response || {};
        const sum = (r.summary || '').toString().substring(0, 400);
        const top = Array.isArray(r.top3) && r.top3.length ? ` Suggested then: ${r.top3.join('; ')}.` : '';
        return `Conversation ${idx + 1} — "${c.topic}": ${sum}${top}`;
      });
      parts.push(`Past conversations about this student (most recent last):\n${prev.join('\n\n')}`);
    }

    return parts.join('\n\n');
  };

  const handleFollowUp = async () => {
    // Screen the follow-up answers too, before any API call.
    if (containsRiskLanguage(`${description} ${fuAnswers.join(' ')}`)) { setSafety(true); return; }
    setError('');
    setLoading(true);
    try {
      contextRef.current = buildContext();
      const result = await getSummary(description, initials, grade, contextRef.current);
      if (!result || !result.summary) throw new Error('Invalid response');
      setResponse(result);
      setStep('response');
      setTab('summary');
      loadBrain(); // pre-load the next section in the background while she reads
    } catch (e: any) {
      // errText logs the real error to the console and returns a warm,
      // teacher-safe message (never a raw error, status code, or account detail).
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  };

  const tryStrategy = (strategy: string, category: 'top3' | 'academic' | 'behavioral' | 'relational') => {
    if (!user) return;
    let studentId = '';
    const existingStudent = findStudentByInitials(user.id, initials, grade);
    if (existingStudent) studentId = existingStudent.id;
    const attempt: StrategyAttempt = {
      id: generateId(),
      studentId,
      userId: user.id,
      strategy,
      category,
      dateStarted: Date.now(),
      checkInDate: Date.now() + 3 * 24 * 60 * 60 * 1000,
      status: 'in-progress',
      conversationTopic: description.substring(0, 120)
    };
    saveStrategyAttempt(user.id, attempt);
    setTriedStrategies(prev => [...prev, strategy]);
  };

  const saveToProfile = () => {
    if (!user || !response) return;
    const fullResponse = { ...response, brainScience: brain || undefined, strategies: strategies || undefined, forYou: forYou || undefined, whenToRefer: referral || undefined, mtss: mtss || undefined };
    const conv: Conversation = { id: generateId(), studentId: '', date: Date.now(), topic: description.substring(0, 60), input: description, followUps, response: fullResponse };
    let s = findStudentByInitials(user.id, initials, grade);
    if (!s) s = { id: generateId(), initials: initials.toUpperCase(), grade, userId: user.id, createdAt: Date.now(), conversations: [], wins: [], trend: 'stable' };
    conv.studentId = s.id;
    s.conversations.push(conv);
    s.trend = s.conversations.length > 2 ? 'improving' : 'stable';
    saveStudent(user.id, s);
    setSaved(true);
  };

  const handleTabClick = (t: Tab) => {
    setTab(t);
    loaderFor(t)();
    const next = TAB_ORDER[TAB_ORDER.indexOf(t) + 1];
    if (next) loaderFor(next)(); // pre-load the next tab in the background
  };

  if (safety) return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>
          Dashboard
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Get help now</span>
        <div style={{ width: 60 }} />
      </div>
      <SafetyInterstitial onBack={() => setSafety(false)} />
      <NavBar navigate={navigate} active="dashboard" />
    </div>
  );

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', gap: 20, padding: 24 }}>
      <div className="spinner" />
      <p style={{ color: 'var(--teal)', fontWeight: 700, fontSize: 16, textAlign: 'center' }}>
        {step === 'followup' ? 'Writing your summary...' : 'Getting your follow-up questions...'}
      </p>
      {step === 'followup' && (
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', maxWidth: 280, lineHeight: 1.7 }}>
          Your summary comes first — the other sections load as you open them.
        </p>
      )}
    </div>
  );

  const tabs: [Tab, string][] = [['summary', 'Summary'], ['brain', 'Brain Science'], ['strategies', 'Strategies'], ['forYou', 'For You'], ['refer', L.referralTab], ['mtss', L.mtssTab]];

  const Spinner = ({ label }: { label: string }) => (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <div className="spinner" />
      <p style={{ color: 'var(--teal)', marginTop: 12, fontWeight: 600 }}>{label}</p>
    </div>
  );

  const RetryBox = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div style={{ background: 'var(--amber-light)', borderRadius: 10, padding: '16px', textAlign: 'center' }}>
      <p style={{ color: 'var(--charcoal)', fontWeight: 600, marginBottom: 10, lineHeight: 1.5 }}>{message || "That section didn't load. Please try again."}</p>
      <button onClick={onRetry} className="btn-small">Retry</button>
    </div>
  );

  return (
    <div>
      <div className="top-bar">
        <button
          onClick={() => { if (step === 'identify') navigate('dashboard'); else if (step === 'describe') setStep('identify'); else if (step === 'followup') setStep('describe'); else resetConversation(); }}
          style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>
          {step === 'identify' ? 'Dashboard' : 'Back'}
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>{L.talk}</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content">
        {step === 'identify' && (
          <div className="animate-in">
            <h2 style={{ marginBottom: 8 }}>Who are we talking about?</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24 }}>{L.isParent ? 'Use a first name or nickname and age or grade only — no last names.' : 'Use initials and grade only — never full names.'}</p>
            <div className="form-group">
              <label>{L.identifyLabel} *</label>
              <input value={initials} onChange={e => setInitials(L.isParent ? e.target.value : e.target.value.toUpperCase())} placeholder={L.identifyPlaceholder} maxLength={L.isParent ? 24 : 6} />
              <p className="form-hint">{L.isParent ? '🔒 First name or nickname only — no last names' : '🔒 Initials only — student privacy is always protected'}</p>
            </div>
            <div className="form-group">
              <label>{L.gradeLabel} *</label>
              <select value={grade} onChange={e => setGrade(e.target.value)}>
                {GRADE_OPTIONS.map(g => <option key={g.value} value={g.value} disabled={g.value === ''}>{g.label}</option>)}
              </select>
            </div>
            {existing && (
              <div className="teal-card" style={{ marginBottom: 16 }}>
                <p style={{ fontWeight: 600, color: 'var(--teal)', marginBottom: 4 }}>Welcome back — I remember {existing.initials}</p>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{existing.conversations.length} conversation{existing.conversations.length !== 1 ? 's' : ''} on record. We will build on what we know.</p>
              </div>
            )}
            <button onClick={handleIdentify} className="btn-primary" disabled={!initials || !grade}>Continue</button>
            <button onClick={() => navigate('urgency')} style={{ background: 'none', color: 'var(--coral)', width: '100%', padding: '14px', marginTop: 10, fontSize: 14, fontWeight: 600, textDecoration: 'underline', border: 'none', cursor: 'pointer' }}>
              This looks serious — I need help RIGHT NOW
            </button>
          </div>
        )}

        {step === 'describe' && (
          <div className="animate-in">
            <h2 style={{ marginBottom: 8 }}>Tell me about {initials.toUpperCase()}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>Speak or type — take your time. There is no wrong answer here.</p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder={L.isParent
                  ? `Example: ${initials || 'my son'} melts down every night at homework time — he yells, throws his pencil, and shuts down. Bedtime has gotten hard too. Nothing I try is working and I feel like I'm failing him.`
                  : `Example: ${initials.toUpperCase() || 'J.T.'} has been explosive every morning during transitions. They scream, knock things off their desk, and refuse to move. Nothing I try is working and I'm honestly dreading coming in.`}
                style={{ minHeight: 160 }} />
              <button className={`mic-btn${recording ? ' recording' : ''}`} onClick={() => recording ? stopRec() : startRec(setDescription, description)}>
                <svg viewBox="0 0 24 24" fill={recording ? 'white' : 'none'} stroke="white" strokeWidth="2" width="22" height="22">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </button>
            </div>
            {recording && <p style={{ color: 'var(--coral)', fontSize: 13, marginTop: 6, fontWeight: 600 }}>🔴 Recording... tap mic to stop</p>}
            <button onClick={handleDescribe} className="btn-primary" style={{ marginTop: 20 }} disabled={!description.trim()}>Analyze This</button>
          </div>
        )}

        {step === 'followup' && (
          <div className="animate-in">
            <h2 style={{ marginBottom: 8 }}>A couple more questions</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>Help me understand {initials.toUpperCase()} a little better. Skip any you cannot answer.</p>
            {followUps.map((q, i) => (
              <div className="form-group" key={i}>
                <label>{q}</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <textarea value={fuAnswers[i]} onChange={e => { const a = [...fuAnswers]; a[i] = e.target.value; setFuAnswers(a); }}
                    placeholder="Type your answer or tap the mic..." style={{ minHeight: 80 }} />
                  <button className={`mic-btn${recording ? ' recording' : ''}`} style={{ width: 44, height: 44 }}
                    onClick={() => recording ? stopRec() : startRec((v) => { const a = [...fuAnswers]; a[i] = v; setFuAnswers(a); }, fuAnswers[i])}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="18" height="18">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
            {error && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '12px 16px', borderRadius: 8, fontSize: 14, marginBottom: 16 }}>{error}</div>}
            <button onClick={handleFollowUp} className="btn-primary">Get My Strategies</button>
            <button onClick={handleFollowUp} style={{ background: 'none', color: 'var(--text-secondary)', width: '100%', padding: '12px', marginTop: 8, fontSize: 13, border: 'none', cursor: 'pointer' }}>Skip and get help now</button>
          </div>
        )}

        {step === 'response' && response && (
          <div className="animate-in">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <span className="mono" style={{ fontSize: 22, color: 'var(--teal)', fontWeight: 500 }}>{initials.toUpperCase()}</span>
              <span className="badge badge-teal">{grade}</span>
              {mtss && mtss.tier && (
                <span className={`badge ${mtss.tier === 1 ? 'badge-green' : mtss.tier === 2 ? 'badge-amber' : 'badge-coral'}`}>
                  MTSS Tier {mtss.tier}
                </span>
              )}
            </div>

            <div className="tabs">
              {tabs.map(([k, l]) => (
                <button key={k} className={`tab${tab === k ? ' active' : ''}`} onClick={() => handleTabClick(k as Tab)}>{l}</button>
              ))}
            </div>

            {tab === 'summary' && (
              <div>
                <div className="teal-card" style={{ marginBottom: 16 }}>
                  <p style={{ fontWeight: 700, color: 'var(--teal)', marginBottom: 10, fontSize: 15 }}>Here's what's likely happening with {initials.toUpperCase()}</p>
                  <p style={{ fontSize: 15, lineHeight: 1.7 }}>{response.summary}</p>
                </div>
                <p style={{ fontWeight: 700, marginBottom: 12, fontSize: 15 }}>Top 3 Things To Try Today</p>
                {response.top3?.map((s: string, i: number) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <div className={`strategy-item${i === 0 ? ' highlight' : ''}`} style={{ marginBottom: 0, borderRadius: triedStrategies.includes(s) ? '8px 8px 0 0' : 8 }}>
                      <span style={{ fontWeight: 700, marginRight: 8 }}>{i + 1}.</span>{s}
                    </div>
                    {!triedStrategies.includes(s) ? (
                      <button onClick={() => tryStrategy(s, 'top3')} style={{ width: '100%', background: i === 0 ? 'rgba(26,107,107,0.15)' : 'var(--teal-light)', color: 'var(--teal)', padding: '8px 14px', borderRadius: '0 0 8px 8px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                        ✋ I'm trying this today — remind me to check in
                      </button>
                    ) : (
                      <div style={{ background: 'var(--green-light)', padding: '8px 14px', borderRadius: '0 0 8px 8px', fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>
                        ✅ Saved — you'll get a check-in in 3 days
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {tab === 'brain' && (
              <div>
                <p style={{ fontWeight: 700, marginBottom: 12, color: 'var(--teal)' }}>The Brain Science</p>
                {brain ? (
                  <p style={{ fontSize: 15, lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>{brain}</p>
                ) : brainErr ? (
                  <RetryBox message={brainErr} onRetry={() => retry('brain', loadBrain)} />
                ) : (
                  <Spinner label="Loading brain science..." />
                )}
              </div>
            )}

            {tab === 'strategies' && (
              <div>
                {strategies ? (
                  [['academic', '📚 Academic'], ['behavioral', '🎯 Behavioral'], ['relational', '💛 Relational']].map(([key, label]) => (
                    <div key={key} style={{ marginBottom: 20 }}>
                      <p style={{ fontWeight: 700, marginBottom: 10, fontSize: 15 }}>{label} Strategies</p>
                      {strategies[key]?.map((s: string, i: number) => (
                        <div key={i} style={{ marginBottom: 8 }}>
                          <div className={`strategy-item${i === 0 ? ' highlight' : ''}`} style={{ marginBottom: 0, borderRadius: triedStrategies.includes(s) ? '8px 8px 0 0' : 8 }}>
                            {i === 0 && <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.25)', padding: '2px 6px', borderRadius: 8, marginRight: 6 }}>TRY TODAY</span>}
                            {s}
                          </div>
                          {!triedStrategies.includes(s) ?
                            <button onClick={() => tryStrategy(s, key as any)} style={{ width: '100%', background: 'var(--teal-light)', color: 'var(--teal)', padding: '8px 14px', borderRadius: '0 0 8px 8px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', textAlign: 'left' }}>✋ I'm trying this today</button> :
                            <div style={{ background: 'var(--green-light)', padding: '8px 14px', borderRadius: '0 0 8px 8px', fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>✅ Check-in saved</div>
                          }
                        </div>
                      ))}
                    </div>
                  ))
                ) : strategiesErr ? (
                  <RetryBox message={strategiesErr} onRetry={() => retry('strategies', loadStrategies)} />
                ) : (
                  <Spinner label="Loading strategies..." />
                )}
              </div>
            )}

            {tab === 'forYou' && (
              <div>
                {forYou ? (
                  <>
                    <div style={{ background: 'var(--green-light)', borderRadius: 12, padding: '20px', marginBottom: 16 }}>
                      <p style={{ fontWeight: 700, color: 'var(--green)', marginBottom: 8 }}>💚 This section is just for you.</p>
                      <p style={{ fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{forYou}</p>
                    </div>
                    <div style={{ background: 'var(--amber-light)', borderRadius: 10, padding: '16px', textAlign: 'center' }}>
                      <p style={{ fontWeight: 700, color: '#b87800' }}>You are not alone in this.</p>
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>What you are doing matters more than you know.</p>
                    </div>
                  </>
                ) : forYouErr ? (
                  <RetryBox message={forYouErr} onRetry={() => retry('forYou', loadForYou)} />
                ) : (
                  <Spinner label="Loading your support..." />
                )}
              </div>
            )}

            {tab === 'refer' && (
              <div>
                <p style={{ fontWeight: 700, marginBottom: 12, color: 'var(--teal)' }}>{L.isParent ? 'When To Get Outside Help' : 'When To Seek Additional Support'}</p>
                {referral ? (
                  <p style={{ fontSize: 15, lineHeight: 1.8, whiteSpace: 'pre-wrap', marginBottom: 20 }}>{referral}</p>
                ) : referralErr ? (
                  <div style={{ marginBottom: 20 }}><RetryBox message={referralErr} onRetry={() => retry('refer', loadReferral)} /></div>
                ) : (
                  <Spinner label={L.isParent ? `Thinking through when it might help to get more support for ${initials}…` : `Taking a careful look at whether ${initials.toUpperCase()} needs more support…`} />
                )}
                <div style={{ background: 'var(--teal-light)', borderRadius: 12, padding: '16px', border: '1px solid var(--teal)' }}>
                  <p style={{ fontWeight: 700, color: 'var(--teal)', marginBottom: 6 }}>{L.isParent ? 'Who to talk to' : 'Who to loop in at school'}</p>
                  <p style={{ fontSize: 14, lineHeight: 1.6 }}>{L.isParent ? "These are observations to bring to people who can help — your child's teacher or school counselor, and your pediatrician or family doctor. They can help you decide together what, if anything, is worth doing next. This tool does not diagnose or provide medical advice." : "Decisions about additional support belong to your school's team. Share what you documented here with your school counselor or student support team, or ask your administrator about next steps."}</p>
                </div>
              </div>
            )}

            {tab === 'mtss' && (
              <div>
                {mtss ? (L.isParent ? (
                  <div>
                    {/* Parent variant: "What to ask the school for" — plain questions, never a tier. */}
                    <div style={{ background: 'var(--teal-light)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, lineHeight: 1.6, color: 'var(--teal)' }}>
                      <strong>How to use this:</strong> questions you can bring to the school — the school's team decides what support your child receives.
                    </div>
                    <p style={{ fontWeight: 700, marginBottom: 10, fontSize: 15 }}>What to ask the school for</p>
                    {(mtss.askSchool || []).map((q: string, i: number) => (
                      <div key={i} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 8, fontSize: 14, display: 'flex', gap: 8 }}>
                        <span style={{ color: 'var(--teal)', fontWeight: 700 }}>•</span>{q}
                      </div>
                    ))}
                    {mtss.why && <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)', marginTop: 12, fontStyle: 'italic' }}>{mtss.why}</p>}
                  </div>
                ) : (
                  <div>
                    <div style={{ background: 'var(--teal-light)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, lineHeight: 1.6, color: 'var(--teal)' }}>
                      <strong>How to use this:</strong> a prompt to help you start the tiered-supports conversation with your team — not a tier assignment.
                    </div>
                    <div style={{ background: mtss.tier === 1 ? 'var(--green-light)' : mtss.tier === 2 ? 'var(--amber-light)' : 'var(--coral-light)', borderRadius: 14, padding: '20px', marginBottom: 20, textAlign: 'center' }}>
                      <p style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: mtss.tier === 1 ? 'var(--green)' : mtss.tier === 2 ? '#b87800' : 'var(--coral)', marginBottom: 8 }}>A starting point for your team</p>
                      <p style={{ fontSize: 15, color: 'var(--charcoal)', marginBottom: 4 }}>These observations look like they could start a</p>
                      <p style={{ fontSize: 30, fontWeight: 800, color: mtss.tier === 1 ? 'var(--green)' : mtss.tier === 2 ? 'var(--amber)' : 'var(--coral)', lineHeight: 1.1 }}>Tier {mtss.tier} conversation</p>
                      <p style={{ fontSize: 13, marginTop: 8, color: 'var(--text-secondary)' }}>{mtss.tier === 1 ? 'Universal support' : mtss.tier === 2 ? 'Targeted support' : 'Intensive support'} — to explore with your team</p>
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      {[{ tier: 3, label: 'Tier 3 — Intensive', pct: '20%', color: 'var(--coral)' }, { tier: 2, label: 'Tier 2 — Targeted', pct: '60%', color: 'var(--amber)' }, { tier: 1, label: 'Tier 1 — Universal', pct: '100%', color: 'var(--green)' }].map(t => (
                        <div key={t.tier} style={{ width: t.pct, margin: '0 auto 4px', background: t.tier === mtss.tier ? t.color : '#e0dbd3', borderRadius: 6, padding: '8px 12px', textAlign: 'center' }}>
                          <p style={{ fontSize: 12, fontWeight: 700, color: t.tier === mtss.tier ? 'white' : 'var(--text-secondary)' }}>{t.label}</p>
                        </div>
                      ))}
                    </div>
                    <p style={{ fontWeight: 700, marginBottom: 8, fontSize: 15 }}>How this lines up for a team conversation</p>
                    <p style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 16, color: 'var(--text-secondary)' }}>{mtss.explanation}</p>
                    <p style={{ fontWeight: 700, marginBottom: 8, fontSize: 15 }}>What your team might weigh</p>
                    {mtss.indicators?.map((ind: string, i: number) => (
                      <div key={i} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 8, fontSize: 14, display: 'flex', gap: 8 }}>
                        <span style={{ color: mtss.tier === 1 ? 'var(--green)' : mtss.tier === 2 ? 'var(--amber)' : 'var(--coral)', fontWeight: 700 }}>•</span>{ind}
                      </div>
                    ))}
                    <p style={{ fontWeight: 700, marginBottom: 8, fontSize: 15, marginTop: 16 }}>Recommended Supports</p>
                    {mtss.supports?.map((sup: string, i: number) => (
                      <div key={i} className="strategy-item">{sup}</div>
                    ))}
                    {mtss.tier >= 2 && (
                      <div style={{ background: 'var(--teal-light)', borderRadius: 12, padding: '16px', marginTop: 16, border: '1px solid var(--teal)' }}>
                        <p style={{ fontWeight: 700, color: 'var(--teal)', marginBottom: 6 }}>Bring this to your school's team</p>
                        <p style={{ fontSize: 14, lineHeight: 1.6 }}>Tier 2 and Tier 3 supports are decided by your school's student support / MTSS team. Share these observations with your school counselor or student support team.</p>
                      </div>
                    )}
                    {mtss.citation && (
                      <p style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--text-secondary)', marginTop: 16, fontStyle: 'italic' }}>{mtss.citation}</p>
                    )}
                  </div>
                )) : mtssErr ? (
                  <RetryBox message={mtssErr} onRetry={() => retry('mtss', loadMtss)} />
                ) : (
                  <Spinner label={L.isParent ? `Thinking through what you could ask ${initials}'s school…` : `Thinking through what to bring to your team about ${initials.toUpperCase()}…`} />
                )}
              </div>
            )}

            <div style={{ marginTop: 24 }}>
              <div className="divider" />
              {!saved ? (
                <button onClick={saveToProfile} className="btn-primary" style={{ marginBottom: 10 }}>
                  Save to {initials.toUpperCase()}'s Profile
                </button>
              ) : (
                <div style={{ background: 'var(--green-light)', borderRadius: 10, padding: '12px', textAlign: 'center', marginBottom: 10 }}>
                  <p style={{ color: 'var(--green)', fontWeight: 600 }}>✓ Saved to {initials.toUpperCase()}'s profile</p>
                </div>
              )}
              <div className="grid-2">
                <button onClick={() => navigate('wins')} className="btn-small">Log a Win 🌟</button>
                <button onClick={resetConversation} className="btn-small">New Student</button>
              </div>
            </div>
            <AiDisclaimer navigate={navigate} />
          </div>
        )}
      </div>
      <NavBar navigate={navigate} active="dashboard" />
    </div>
  );
}
